import { useEffect, useMemo, useRef, useState } from 'react';

const retryableSrc = (src) => src && !/^(data:|blob:)/i.test(src);

const withRetryParam = (src, attempt) => {
  if (!src || !attempt || !retryableSrc(src)) return src;
  return `${src}${src.includes('?') ? '&' : '?'}img_retry=${attempt}`;
};

export default function SafeImage({
  src,
  alt = '',
  className = '',
  fallback = null,
  retry = 1,
  retryDelay = 650,
  decoding = 'async',
  onError,
  ...props
}) {
  const cleanSrc = String(src || '').trim();
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(!cleanSrc);
  const timerRef = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setFailed(!cleanSrc);
    if (timerRef.current) clearTimeout(timerRef.current);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [cleanSrc]);

  const currentSrc = useMemo(() => withRetryParam(cleanSrc, attempt), [cleanSrc, attempt]);

  if (!cleanSrc || failed) return fallback;

  return (
    <img
      {...props}
      src={currentSrc}
      alt={alt}
      className={className}
      decoding={decoding}
      onError={(event) => {
        if (typeof onError === 'function') onError(event);
        if (attempt < retry && retryableSrc(cleanSrc)) {
          timerRef.current = setTimeout(() => setAttempt(value => value + 1), retryDelay);
          return;
        }
        setFailed(true);
      }}
    />
  );
}
